// src/components/roles/RolesTable/RolesTable.tsx
import React, { useCallback, useMemo } from 'react';
import { CircularProgress, Box, Tooltip } from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Role } from '../../../types/role.types';
import { useRolesTable, UseRolesTableProps } from './RolesTable.hooks';
import {
  LoadingContainer,
  RoleName,
  UsersBadge,
  SystemBadge,
  ActionsContainer,
} from './RolesTable.styles';
import { BaseTable, TableColumn } from '../../common/Table';
import { TableStatusChip } from '../../common/Table/TableStatusChip';
import { ActionButton } from '../../common/Table/Table.styles';
import InlineBlockConfirmation from './InlineBlockConfirmation';
import { useHasFlagPermission } from '../../../hooks/useHasFlagPermission';
import { FlagSwitch } from '../../../components/ui';

export interface RolesTableProps extends UseRolesTableProps {
  loading: boolean;
  className?: string;
  onBlockRoleDirect?: (id: string) => Promise<void>;
  onUnblockRoleDirect?: (id: string) => Promise<void>;
}

const RolesTableComponent: React.FC<RolesTableProps> = ({
  roles,
  loading,
  selectedRoles,
  setSelectedRoles,
  onViewRole,
  onEditRole,
  onDeleteRole,
  onCopyRole,
  onBlockRole,
  onBlockRoleDirect,
  onUnblockRoleDirect,
  isSuperAdmin = false,
  className,
}) => {
  // Verificar permiso para el flag de bloqueo
  const canBlockRoles = useHasFlagPermission('settings', 'roles-management', 'block');

  const {
    getStatusDisplay,
    getStatusColor,
    checkIsSystemRole,
    formatUserCount,
    formatDate,
    handleViewRole,
    handleEditRole,
    handleDeleteRole,
    handleCopyRole,
    canDeleteRole,
    canEditRole,
    handleSwitchChange,
    handleConfirmBlock,
    handleCancelBlock,
    isBlocking,
    isConfirmingBlock,
    getSwitchAnchor,
    getConfirmationMessage,
    getActionType,
  } = useRolesTable({
    roles,
    selectedRoles,
    setSelectedRoles,
    onViewRole,
    onEditRole,
    onDeleteRole,
    onCopyRole,
    onBlockRole,
    onBlockRoleDirect,
    onUnblockRoleDirect,
    isSuperAdmin,
  });

  const columns: TableColumn<Role>[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Nombre del Rol',
        sortAccessor: (item) => item.name,
        render: (item) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RoleName>{item.name}</RoleName>
            {checkIsSystemRole(item) && (
              <SystemBadge
                label="Sistema"
                size="small"
                title="Este es un rol del sistema y no puede ser eliminado"
              />
            )}
          </Box>
        ),
      },
      {
        id: 'users',
        label: 'Usuarios',
        sortAccessor: (item) => item.userCount,
        render: (item) => (
          <UsersBadge>
            <PeopleIcon fontSize="small" />
            <span>{formatUserCount(item.userCount)}</span>
          </UsersBadge>
        ),
      },
      {
        id: 'updatedAt',
        label: 'Última Actualización',
        sortAccessor: (item) => item.updatedAt,
        render: (item) => (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {formatDate(item.updatedAt)}
          </Box>
        ),
      },
      {
        id: 'status',
        label: 'Estado',
        sortAccessor: (item) => item.isActive,
        render: (item) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {canBlockRoles && (
              <Box sx={{ mr: 1 }}>
                <FlagSwitch
                  checked={item.isActive}
                  onChange={(e, checked) => {
                    handleSwitchChange(item, checked, e.currentTarget);
                  }}
                  disabled={
                    checkIsSystemRole(item) || isBlocking(item.id) || isConfirmingBlock(item.id)
                  }
                  activeColor={item.isActive ? 'success' : undefined}
                />
              </Box>
            )}
            <TableStatusChip
              label={getStatusDisplay(item.isActive)}
              color={getStatusColor(item.isActive)}
              visualStyle="quiet"
              withIcon={false}
            />
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getStatusDisplay,
      getStatusColor,
      checkIsSystemRole,
      formatUserCount,
      formatDate,
      canBlockRoles,
      handleSwitchChange,
      handleConfirmBlock,
      handleCancelBlock,
      isBlocking,
      isConfirmingBlock,
      getSwitchAnchor,
    ],
  );

  const handleSelectItems = useCallback(
    (items: (string | number)[]) => setSelectedRoles(items as string[]),
    [setSelectedRoles],
  );

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <>
      <BaseTable<Role>
        className={className}
        items={roles}
        columns={columns}
        selectedItems={selectedRoles}
        onSelectItems={handleSelectItems}
        showCheckbox
        showActions
        getItemId={(item) => item.id}
        emptyStateTitle="No se encontraron roles"
        emptyStateSubtitle="Ajuste los filtros o cree un nuevo rol."
        renderActions={(role) => (
          <ActionsContainer>
            {onCopyRole && (
              <Tooltip title="Copiar configuración de permisos" arrow>
                <ActionButton
                  size="small"
                  onClick={() => handleCopyRole(role.id)}
                  sx={{ color: 'info.main' }}
                >
                  <CopyIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            )}
            {onViewRole && (
              <Tooltip title="Ver detalles" arrow>
                <ActionButton
                  size="small"
                  onClick={() => handleViewRole(role.id)}
                  sx={{ color: 'info.main' }}
                >
                  <ViewIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            )}
            {onEditRole && canEditRole(role) && (
              <Tooltip title="Editar" arrow>
                <ActionButton
                  size="small"
                  onClick={() => handleEditRole(role.id)}
                  sx={{ color: 'primary.main' }}
                >
                  <EditIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            )}
            {onDeleteRole && canDeleteRole(role) && (
              <Tooltip
                title={
                  checkIsSystemRole(role)
                    ? 'No se puede eliminar un rol del sistema'
                    : 'Eliminar rol'
                }
                arrow
              >
                <ActionButton
                  size="small"
                  onClick={() => handleDeleteRole(role.id)}
                  disabled={checkIsSystemRole(role)}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            )}
          </ActionsContainer>
        )}
      />
      {/* Renderizar popovers de confirmación para cada rol que lo necesite */}
      {canBlockRoles &&
        roles.map((role) => {
          if (isConfirmingBlock(role.id)) {
            return (
              <InlineBlockConfirmation
                key={`block-confirmation-${role.id}`}
                open={isConfirmingBlock(role.id)}
                anchorEl={getSwitchAnchor(role.id)}
                onClose={handleCancelBlock}
                onConfirm={() => handleConfirmBlock(role.id)}
                onCancel={handleCancelBlock}
                isBlocking={isBlocking(role.id)}
                message={getConfirmationMessage(role.id)}
                actionType={getActionType() || undefined}
              />
            );
          }
          return null;
        })}
    </>
  );
};

export default RolesTableComponent;
